<template>
    <div class="google-location-tab">
        <b-overlay
            :show="is_load"
            rounded="sm"
        >
            <div v-if="!is_load">
                <!-- Header -->
                <div
                    class="google-header mb-4"
                >
                    <div
                        class="d-flex justify-content-between align-items-start"
                    >
                        <div>
                            <div
                                class="d-flex align-items-center mb-1"
                            >
                                <i
                                    class="fab fa-google fs-xxl mr-2"
                                ></i>

                                <h3 class="mb-0">
                                    {{
                                        google.title ||
                                        location.title
                                    }}
                                </h3>
                            </div>

                            <div
                                v-if="address_text"
                                class="text-muted"
                            >
                                <i
                                    class="fal fa-map-marker-alt mr-1"
                                ></i>

                                {{ address_text }}
                            </div>
                        </div>

                        <div
                            class="google-buttons"
                        >
                            <a
                                v-if="maps_url"
                                :href="maps_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="btn btn-outline-primary btn-sm mr-2"
                            >
                                <i
                                    class="fal fa-map-marker-alt mr-1"
                                ></i>

                                Google Maps
                            </a>

                            <a
                                v-if="new_review_url"
                                :href="new_review_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="btn btn-outline-warning btn-sm"
                            >
                                <i
                                    class="fal fa-star mr-1"
                                ></i>

                                {{ __t('WRITE_REVIEW') }}
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Main info -->
                <div class="row">
                    <div class="col-md-6">
                        <div
                            class="google-card mb-3"
                        >
                            <div
                                class="google-card-title"
                            >
                                <i
                                    class="fal fa-map-marker-alt mr-2"
                                ></i>

                                {{ __t('ADDRESS') }}
                            </div>

                            <div
                                v-if="address_text"
                                class="google-card-value"
                            >
                                {{ address_text }}
                            </div>

                            <div
                                v-else
                                class="text-muted"
                            >
                                —
                            </div>
                        </div>
                    </div>

                    <div class="col-md-6">
                        <div
                            class="google-card mb-3"
                        >
                            <div
                                class="google-card-title"
                            >
                                <i
                                    class="fal fa-globe mr-2"
                                ></i>

                                {{ __t('WEBSITE') }}
                            </div>

                            <div
                                v-if="google.website_url"
                                class="google-card-value"
                            >
                                <a
                                    :href="google.website_url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {{
                                        google.website_url
                                    }}

                                    <i
                                        class="fal fa-external-link ml-1"
                                    ></i>
                                </a>
                            </div>

                            <div
                                v-else
                                class="text-muted"
                            >
                                —
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Phones -->
                <div
                    class="google-card mb-3"
                >
                    <div
                        class="google-card-title"
                    >
                        <i
                            class="fal fa-phone mr-2"
                        ></i>

                        {{ __t('PHONE') }}
                    </div>

                    <div
                        v-if="primary_phone"
                        class="mb-1"
                    >
                        <a
                            :href="'tel:' + primary_phone"
                        >
                            {{ primary_phone }}
                        </a>

                        <small
                            class="text-muted ml-2"
                        >
                            {{ __t('PRIMARY') }}
                        </small>
                    </div>

                    <div
                        v-for="phone in additional_phones"
                        :key="phone"
                        class="mb-1"
                    >
                        <a
                            :href="'tel:' + phone"
                        >
                            {{ phone }}
                        </a>
                    </div>

                    <div
                        v-if="
                            !primary_phone &&
                            additional_phones.length === 0
                        "
                        class="text-muted"
                    >
                        —
                    </div>
                </div>

                <!-- Hours -->
                <div
                    class="google-card mb-3"
                >
                    <div
                        class="google-card-title mb-3"
                    >
                        <i
                            class="fal fa-clock mr-2"
                        ></i>

                        {{ __t('WORKING_HOURS') }}
                    </div>

                    <div
                        v-for="row in working_hours"
                        :key="row.day"
                        class="hours-row"
                    >
                        <div
                            class="hours-day"
                        >
                            {{ row.title }}
                        </div>

                        <div
                            class="hours-time"
                            :class="{
                                'text-muted':
                                    !row.hasHours
                            }"
                        >
                            <template
                                v-if="row.hasHours"
                            >
                                {{
                                    row.open
                                }}
                                —
                                {{
                                    row.close
                                }}
                            </template>

                            <template v-else>
                                {{ __t('CLOSED') }}
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Google IDs -->
                <div
                    class="google-card google-meta mb-3"
                >
                    <div
                        class="google-card-title mb-3"
                    >
                        <i
                            class="fal fa-info-circle mr-2"
                        ></i>

                        Google
                    </div>

                    <div class="row">
                        <div class="col-md-6 mb-2">
                            <small
                                class="text-muted d-block"
                            >
                                Place ID
                            </small>

                            <code>
                                {{
                                    google.place_id ||
                                    "—"
                                }}
                            </code>
                        </div>

                        <div class="col-md-6 mb-2">
                            <small
                                class="text-muted d-block"
                            >
                                Location ID
                            </small>

                            <code>
                                {{
                                    location_id ||
                                    "—"
                                }}
                            </code>
                        </div>

                        <div class="col-md-6">
                            <small
                                class="text-muted d-block"
                            >
                                Store Code
                            </small>

                            <span>
                                {{
                                    store_code ||
                                    "—"
                                }}
                            </span>
                        </div>

                        <div class="col-md-6">
                            <small
                                class="text-muted d-block"
                            >
                                Status
                            </small>

                            <span
                                v-if="location_status"
                                class="badge"
                                :class="
                                    location_status === 'OPEN'
                                        ? 'badge-success'
                                        : 'badge-secondary'
                                "
                            >
                                {{
                                    location_status
                                }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </b-overlay>
    </div>
</template>

<script>
export default {
    name: "review_google_tab",

    props: {
        location: {
            type: Object,

            default() {
                return {};
            }
        },

        provider_id: {
            default: 0
        }
    },

    data() {
        return {
            is_load: false,

            google: {}
        };
    },

    mounted() {
        this.load_google_info();
    },

    watch: {
        "location.id": function (
            newValue,
            oldValue
        ) {
            if (
                newValue &&
                newValue !== oldValue
            ) {
                this.load_google_info();
            }
        }
    },

    methods: {
        async load_google_info() {
            if (!this.location || !this.location.id) {
                return;
            }

            this.is_load = true;

            await review_api.get("/locations/" + this.location.id + "/ext")
                .then(({ data }) => {
                    /*
                     * Поддерживаем оба варианта:
                     *
                     * {
                     *   status,
                     *   data: {...}
                     * }
                     *
                     * и непосредственно {...}
                     */

                    if (
                        data &&
                        data.status ===
                        "operation_success"
                    ) {
                        this.google =
                            data.data || {};
                    } else {
                        this.google =
                            data || {};
                    }
                })
                .catch(({ response }) => {
                    EventBus.$emit(
                        "show_toast",
                        {
                            err:
                                response &&
                                response.data
                                    ? response.data
                                        .message
                                    : this.__t(
                                        "ERROR"
                                    ),

                            type:
                                "danger",

                            title:
                                this.__t(
                                    "ERROR"
                                )
                        }
                    );
                })
                .finally(() => {
                    this.is_load = false;
                });
        },

        pad_time(value) {
            let number =
                parseInt(value || 0);

            return number
                .toString()
                .padStart(2, "0");
        },

        format_time(time) {
            if (!time) {
                return "";
            }

            const hours =
                this.pad_time(
                    time.hours
                );

            const minutes =
                this.pad_time(
                    time.minutes || 0
                );

            return (
                hours +
                ":" +
                minutes
            );
        }
    },

    computed: {
        maps_url() {
            /*
             * Лучше raw.metadata.mapsUri,
             * т.к. Google реально его нам отдал.
             */
            return (
                this.google.maps_url ||
                (
                    this.google.raw &&
                    this.google.raw.metadata
                        ? this.google.raw
                            .metadata
                            .mapsUri
                        : ""
                ) ||
                ""
            );
        },

        new_review_url() {
            if (
                !this.google.raw ||
                !this.google.raw.metadata
            ) {
                return "";
            }

            return (
                this.google.raw
                    .metadata
                    .newReviewUri ||
                ""
            );
        },

        address_text() {
            const address =
                this.google.address || {};

            const lines =
                Array.isArray(
                    address.addressLines
                )
                    ? address.addressLines
                    : [];

            if (lines.length > 0) {
                return lines.join(", ");
            }

            const parts = [
                address.postalCode,
                address.locality,
                address.administrativeArea
            ].filter(Boolean);

            return parts.join(", ");
        },

        primary_phone() {
            return (
                this.google
                    .phone_numbers &&
                this.google
                    .phone_numbers
                    .primaryPhone
            ) || "";
        },

        additional_phones() {
            if (
                !this.google
                    .phone_numbers ||
                !Array.isArray(
                    this.google
                        .phone_numbers
                        .additionalPhones
                )
            ) {
                return [];
            }

            return this.google
                .phone_numbers
                .additionalPhones;
        },

        location_id() {
            if (this.google.raw && this.google.raw.name) {
                const match =
                    this.google.raw.name.match(
                        /locations\/([^/]+)/
                    );

                if (
                    match &&
                    match[1]
                ) {
                    return match[1];
                }
            }

            return "";
        },

        store_code() {
            if (
                !this.google.raw
            ) {
                return "";
            }

            return (
                this.google.raw
                    .storeCode || ""
            );
        },

        location_status() {
            if (
                !this.google.raw ||
                !this.google.raw.openInfo
            ) {
                return "";
            }

            return (
                this.google.raw
                    .openInfo
                    .status || ""
            );
        },

        working_hours() {
            const dayOrder = [
                "MONDAY",
                "TUESDAY",
                "WEDNESDAY",
                "THURSDAY",
                "FRIDAY",
                "SATURDAY",
                "SUNDAY"
            ];

            const titles = {
                MONDAY:
                    this.__t("MONDAY"),

                TUESDAY:
                    this.__t("TUESDAY"),

                WEDNESDAY:
                    this.__t("WEDNESDAY"),

                THURSDAY:
                    this.__t("THURSDAY"),

                FRIDAY:
                    this.__t("FRIDAY"),

                SATURDAY:
                    this.__t("SATURDAY"),

                SUNDAY:
                    this.__t("SUNDAY")
            };

            const periods =
                Array.isArray(
                    this.google
                        .regular_hours
                )
                    ? this.google
                        .regular_hours
                    : [];

            return dayOrder.map(
                day => {
                    const period =
                        periods.find(
                            item =>
                                item.openDay ===
                                day
                        );

                    if (!period) {
                        return {
                            day: day,
                            title:
                                titles[day] ||
                                day,

                            open: "",
                            close: "",

                            hasHours:
                                false
                        };
                    }

                    return {
                        day: day,

                        title:
                            titles[day] ||
                            day,

                        open:
                            this.format_time(
                                period.openTime
                            ),

                        close:
                            this.format_time(
                                period.closeTime
                            ),

                        hasHours: true
                    };
                }
            );
        }
    }
};
</script>

<style scoped>
.google-location-tab {
    padding: 5px;
}

.google-header {
    padding: 15px;
    background: #f8f9fa;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
}

.google-card {
    padding: 15px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
}

.google-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
}

.google-card-value {
    margin-top: 8px;
    font-size: 15px;
}

.hours-row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    min-height: 38px;

    padding: 6px 0;

    border-bottom: 1px solid #eeeeee;
}

.hours-row:last-child {
    border-bottom: 0;
}

.hours-day {
    width: 150px;
    font-weight: 500;
}

.hours-time {
    font-weight: 600;
}

.google-meta code {
    word-break: break-all;
}

.google-buttons {
    white-space: nowrap;
}
</style>
